'use client'

import { Slot } from '@radix-ui/react-slot'
import { ChevronDown, Menu as MenuIcon, Search } from 'lucide-react'
import * as React from 'react'

import { APP_CHROME_HEIGHT_CLASS } from '../app-chrome'
import { cn } from '../cn'
import { useShellLayout, useSidebarState } from './app-shell'
import { Avatar, AvatarFallback, AvatarImage } from './avatar'

type Breakpoint = 'md' | 'lg'

export interface AppTopBarProps extends React.HTMLAttributes<HTMLElement> {
  size?: 'sm' | 'md'
  sticky?: boolean
}

const AppTopBar = React.forwardRef<HTMLElement, AppTopBarProps>(
  ({ size: _size, sticky = true, className, children, ...props }, ref) => {
    // The unified shell swaps the bar for `MobileTopRow` on phones.
    const unified = useShellLayout() === 'unified'
    return (
      <header
        ref={ref}
        className={cn(
          'relative z-50 flex shrink-0 items-center justify-between gap-x-2 border-b border-border bg-card px-2',
          APP_CHROME_HEIGHT_CLASS,
          sticky && 'sticky top-0',
          unified && 'hidden md:flex',
          className,
        )}
        {...props}>
        {children}
      </header>
    )
  },
)
AppTopBar.displayName = 'AppTopBar'

const AppTopBarLeft = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-x-2', className)} {...props}>
      {children}
    </div>
  ),
)
AppTopBarLeft.displayName = 'AppTopBarLeft'

const AppTopBarRight = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    // Unified shell: pinned right even when nothing to its left fills the bar
    // (an empty app row, e.g. a single-app user or an unreachable registry);
    // otherwise the bell drifted into the middle.
    const unified = useShellLayout() === 'unified'
    return (
      <div ref={ref} className={cn('flex items-center gap-x-2', unified && 'ml-auto', className)} {...props}>
        {children}
      </div>
    )
  },
)
AppTopBarRight.displayName = 'AppTopBarRight'

export interface AppTopBarHamburgerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  hideAt?: Breakpoint
}

const hamburgerHideClass = (hideAt: Breakpoint) => (hideAt === 'lg' ? 'lg:hidden' : 'md:hidden')

const AppTopBarHamburger = React.forwardRef<HTMLButtonElement, AppTopBarHamburgerProps>(
  ({ asChild, hideAt = 'md', className, onClick, children, ...props }, ref) => {
    // Auto-bind to AppShell's sidebar context when no explicit onClick is
    // passed. This is the headline DRY fix — the hamburger Just Works in
    // every app that wraps in <AppShell>, no useState wiring required.
    const sidebarState = useSidebarState()
    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (onClick) {
          onClick(event)
          if (event.defaultPrevented) {
            return
          }
        }
        if (sidebarState && !onClick) {
          sidebarState.setOpen(true)
        }
      },
      [onClick, sidebarState],
    )

    const classes = cn(
      'inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground/70 hover:bg-muted hover:text-foreground transition-colors',
      hamburgerHideClass(hideAt),
      className,
    )
    if (asChild) {
      return (
        <Slot ref={ref} className={classes} onClick={handleClick} {...props}>
          {children as React.ReactElement}
        </Slot>
      )
    }
    return (
      <button ref={ref} type="button" className={classes} aria-label="Open navigation" onClick={handleClick} {...props}>
        {children ?? <MenuIcon className="h-5 w-5" />}
      </button>
    )
  },
)
AppTopBarHamburger.displayName = 'AppTopBarHamburger'

export interface AppTopBarBrandProps extends React.HTMLAttributes<HTMLDivElement> {
  href?: string
  asChild?: boolean
}

const AppTopBarBrand = React.forwardRef<HTMLDivElement, AppTopBarBrandProps>(
  ({ href, asChild, className, children, ...props }, ref) => {
    const classes = cn('flex items-center gap-2 text-foreground', className)
    if (asChild) {
      return (
        <Slot ref={ref as React.Ref<HTMLElement>} className={classes} {...(props as React.HTMLAttributes<HTMLElement>)}>
          {children as React.ReactElement}
        </Slot>
      )
    }
    if (href) {
      return (
        <a
          ref={ref as unknown as React.Ref<HTMLAnchorElement>}
          href={href}
          className={classes}
          {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>
          {children}
        </a>
      )
    }
    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    )
  },
)
AppTopBarBrand.displayName = 'AppTopBarBrand'

export interface AppTopBarSearchTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  placeholder?: string
  kbdHint?: React.ReactNode
  icon?: React.ReactNode
  fullWidth?: boolean
}

const AppTopBarSearchTrigger = React.forwardRef<HTMLButtonElement, AppTopBarSearchTriggerProps>(
  ({ placeholder = 'Search...', kbdHint = '⌘K', icon, fullWidth, className, ...props }, ref) => (
    <div className={cn('flex flex-1 items-center', fullWidth ? 'justify-stretch' : 'justify-center')}>
      <button
        ref={ref}
        type="button"
        className={cn(
          'flex h-9 w-full items-center gap-2 rounded-lg border border-border bg-muted px-3 text-sm text-muted-foreground transition-colors hover:bg-muted/70',
          !fullWidth && 'max-w-sm',
          className,
        )}
        {...props}>
        {icon ?? <Search className="h-4 w-4" />}
        <span className="flex-1 text-left">{placeholder}</span>
        {kbdHint != null && (
          <kbd
            aria-hidden
            className="hidden rounded bg-background/70 px-1.5 py-0.5 text-xs font-medium text-muted-foreground sm:inline-block">
            {kbdHint}
          </kbd>
        )}
      </button>
    </div>
  ),
)
AppTopBarSearchTrigger.displayName = 'AppTopBarSearchTrigger'

export interface AppTopBarIconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode
  unread?: boolean
  asChild?: boolean
}

const AppTopBarIconButton = React.forwardRef<HTMLButtonElement, AppTopBarIconButtonProps>(
  ({ icon, unread, asChild, className, children, ...props }, ref) => {
    const classes = cn(
      // Same box and tone as the shared notification bell (NotificationCenter)
      // and the top bar's search button, so every top-bar icon reads as one
      // set: 36px, muted icon, muted hover wash. Pass a 20px (h-5 w-5) icon.
      'relative inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:pointer-events-none disabled:opacity-40',
      className,
    )
    const inner = (
      <>
        {icon}
        {unread && <span aria-hidden className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-destructive" />}
        {children}
      </>
    )
    if (asChild) {
      return (
        <Slot ref={ref} className={classes} {...props}>
          {children as React.ReactElement}
        </Slot>
      )
    }
    return (
      <button ref={ref} type="button" className={classes} {...props}>
        {inner}
      </button>
    )
  },
)
AppTopBarIconButton.displayName = 'AppTopBarIconButton'

export interface AppTopBarUserTriggerProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'name'> {
  name?: string | null
  email?: string | null
  imageSrc?: string | null
  initials?: string
  showNameAt?: 'md' | 'lg' | 'never'
  asChild?: boolean
}

const computeInitials = (name?: string | null, email?: string | null) => {
  if (name) {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return email?.charAt(0).toUpperCase() ?? '?'
}

const nameShowClass = (showNameAt: 'md' | 'lg' | 'never') =>
  showNameAt === 'never' ? 'hidden' : showNameAt === 'lg' ? 'hidden lg:flex' : 'hidden md:flex'

const AppTopBarUserTrigger = React.forwardRef<HTMLButtonElement, AppTopBarUserTriggerProps>(
  ({ name, email, imageSrc, initials, showNameAt = 'md', asChild, className, children, ...props }, ref) => {
    const fallbackInitials = initials ?? computeInitials(name, email)
    const label = name ?? email ?? 'User'
    const classes = cn(
      'inline-flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-muted transition-colors',
      className,
    )
    const content = children ?? (
      <>
        <Avatar className="h-8 w-8">
          {imageSrc ? <AvatarImage src={imageSrc} alt={label} /> : null}
          <AvatarFallback>{fallbackInitials}</AvatarFallback>
        </Avatar>
        <span className={cn('items-center gap-2', nameShowClass(showNameAt))}>
          <span className="text-sm font-semibold">{label}</span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </span>
      </>
    )
    if (asChild) {
      return (
        <Slot ref={ref} className={classes} {...props}>
          <button type="button">{content}</button>
        </Slot>
      )
    }
    return (
      <button ref={ref} type="button" className={classes} {...props}>
        {content}
      </button>
    )
  },
)
AppTopBarUserTrigger.displayName = 'AppTopBarUserTrigger'

export interface AppTopBarSeparatorProps {
  hideAt?: Breakpoint
  showAt?: Breakpoint
  className?: string
}

const AppTopBarSeparator: React.FC<AppTopBarSeparatorProps> = ({ hideAt, showAt, className }) => (
  <span
    aria-hidden
    className={cn(
      'h-6 w-px bg-border',
      hideAt === 'lg' ? 'lg:hidden' : hideAt === 'md' ? 'md:hidden' : undefined,
      showAt === 'lg' ? 'hidden lg:block' : showAt === 'md' ? 'hidden md:block' : undefined,
      className,
    )}
  />
)

export {
  AppTopBar,
  AppTopBarBrand,
  AppTopBarHamburger,
  AppTopBarIconButton,
  AppTopBarLeft,
  AppTopBarRight,
  AppTopBarSearchTrigger,
  AppTopBarSeparator,
  AppTopBarUserTrigger,
}
